/** Product-card gallery. Inactive media is hydrated only on interaction. */
if (!customElements.get("card-gallery")) {
  customElements.define("card-gallery", class CardGallery extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute("data-card-gallery") || this._connected) return;
      this.track = this.querySelector("[data-gallery-track]");
      if (!this.track) return;
      this._connected = true;
      this.slides = Array.from(this.track.children);
      this.prevBtn = this.querySelector("[data-gallery-prev]");
      this.nextBtn = this.querySelector("[data-gallery-next]");
      this.buttons = this.querySelector("[data-gallery-buttons]");
      this.indicator = this.querySelector("[data-gallery-indicator]");
      this.status = this.querySelector("[data-gallery-status]");
      this.ofWord = this.dataset.galleryOf || "/";
      this.segments = Array.from(this.querySelectorAll("[data-gallery-indicator] > *"));
      this.index = 0;
      this.slideWidth = 0;
      this._ticking = false;
      this._announce = false;
      this.onScrollBound = this.onScroll.bind(this);
      this.onKeydownBound = this.onKeydown.bind(this);
      this.onPrev = (event) => this.onArrow(event, -1);
      this.onNext = (event) => this.onArrow(event, 1);
      this.onIntent = () => {
        this.hydrateSlide(this.index - 1);
        this.hydrateSlide(this.index + 1);
      };
      this.stopStart = (event) => event.stopPropagation();
      if (window.matchMedia("(pointer: coarse)").matches) {
        this.track.classList.add("swiper-no-swiping");
        this.track.addEventListener("pointerdown", this.stopStart);
        this.track.addEventListener("touchstart", this.stopStart, { passive: true });
      }
      this.track.addEventListener("scroll", this.onScrollBound, { passive: true });
      this.track.addEventListener("pointerdown", this.onIntent, { passive: true });
      this.addEventListener("pointerenter", this.onIntent, { passive: true });
      this.addEventListener("focusin", this.onIntent);
      this.addEventListener("keydown", this.onKeydownBound);
      this.prevBtn?.addEventListener("click", this.onPrev);
      this.nextBtn?.addEventListener("click", this.onNext);
      if ("ResizeObserver" in window) {
        this._resizeObserver = new ResizeObserver(() => this.layout());
        this._resizeObserver.observe(this.track);
      }
      this.layout();
      this.hydrateSlide(0);
      this.update();
      this._announce = true;
    }

    disconnectedCallback() {
      this.track?.removeEventListener("scroll", this.onScrollBound);
      this.track?.removeEventListener("pointerdown", this.onIntent);
      this.track?.removeEventListener("pointerdown", this.stopStart);
      this.track?.removeEventListener("touchstart", this.stopStart);
      this.removeEventListener("pointerenter", this.onIntent);
      this.removeEventListener("focusin", this.onIntent);
      this.removeEventListener("keydown", this.onKeydownBound);
      this.prevBtn?.removeEventListener("click", this.onPrev);
      this.nextBtn?.removeEventListener("click", this.onNext);
      this._resizeObserver?.disconnect();
      this._connected = false;
    }

    layout() {
      // A tab can initialize while hidden. Keep the previous width until visible.
      const width = this.track.clientWidth;
      if (width > 0) this.slideWidth = width;
    }

    hydrateSlide(index) {
      const template = this.slides[index]?.querySelector("template[data-gallery-media]");
      if (!template) return;
      const fragment = template.content.cloneNode(true);
      const width = this.track.clientWidth || this.slideWidth;
      fragment.querySelectorAll("img").forEach((img) => {
        // Resolve sizes before srcset is attached to the live document.
        img.sizes = width > 0 ? `${Math.ceil(width)}px` : img.sizes.replace(/^auto,\s*/, "");
        img.loading = "eager";
      });
      template.replaceWith(fragment);
    }

    onArrow(event, direction) {
      event.preventDefault();
      this.goTo(this.index + direction);
      if (event.detail !== 0) event.currentTarget.blur();
    }

    onKeydown(event) {
      if (this.slides.length < 2 || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest(".card-pro__qbp")) return;
      let target;
      switch (event.key) {
        case "ArrowLeft": target = this.index - 1; break;
        case "ArrowRight": target = this.index + 1; break;
        case "Home": target = 0; break;
        case "End": target = this.slides.length - 1; break;
        default: return;
      }
      event.preventDefault();
      this.goTo(target);
    }

    onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      requestAnimationFrame(() => {
        const width = this.track.clientWidth || this.slideWidth || 1;
        const position = this.track.scrollLeft / width;
        // Cover both partly visible slides during touch scrolling.
        this.hydrateSlide(Math.floor(position));
        this.hydrateSlide(Math.ceil(position));
        const next = Math.max(0, Math.min(Math.round(position), this.slides.length - 1));
        if (next !== this.index) {
          this.index = next;
          this.update();
        }
        this._ticking = false;
      });
    }

    goTo(target) {
      const index = Math.max(0, Math.min(target, this.slides.length - 1));
      this.hydrateSlide(index);
      const width = this.track.clientWidth || this.slideWidth;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.track.scrollTo({ left: index * width, behavior: reduce ? "auto" : "smooth" });
    }

    update() {
      const last = this.slides.length - 1;
      const single = this.slides.length < 2;
      this.segments.forEach((segment, i) => segment.classList.toggle("is-active", i === this.index));
      const prevDisabled = this.index <= 0;
      const nextDisabled = this.index >= last;
      if (prevDisabled && this.prevBtn && document.activeElement === this.prevBtn) {
        this.nextBtn?.focus();
      } else if (nextDisabled && this.nextBtn && document.activeElement === this.nextBtn) {
        this.prevBtn?.focus();
      }
      this.prevBtn?.toggleAttribute("disabled", prevDisabled);
      this.nextBtn?.toggleAttribute("disabled", nextDisabled);
      this.buttons?.classList.toggle("card-pro__gallery-buttons--hidden", single);
      this.indicator?.classList.toggle("card-pro__gallery-indicator--hidden", single);
      if (this.status && this._announce && !single) {
        this.status.textContent = `${this.index + 1} ${this.ofWord} ${this.slides.length}`;
      }
    }

    refresh() {
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.segments = Array.from(this.querySelectorAll("[data-gallery-indicator] > *"));
      this.index = 0;
      this.slideWidth = 0;
      this.track.scrollLeft = 0;
      this.layout();
      this.hydrateSlide(0);
      this.update();
    }
  });
}
